/**
 * Launch-navigation coordination for native cold starts.
 *
 * A universal link cannot start the WebView anywhere but `server.url`:
 * Capacitor loads `/`, the proxy sends a signed-in user to `/hiives`, and
 * only once React has hydrated can `App.getLaunchUrl()` tell us the app was
 * opened on `/l/mHDNQ7dt`. So a deep link is always followed AFTER the start
 * page has rendered.
 *
 * The splash does NOT wait for that decision anymore: it releases on the
 * first painted frame (see native-splash-ready.client.tsx), so a deep-link
 * launch briefly shows the start page's skeleton before navigating. That
 * flash is accepted deliberately — holding the logo over the probe made
 * every launch, deep link or not, pay its full cost.
 *
 * What this gate still does: let the deep-link handler tell any waiter that
 * launch routing has settled (a navigation was started, or there is nothing
 * to follow). Releases are idempotent; the native shells run their own
 * safety caps (~3.5s) regardless, so the gate can never strand the splash.
 *
 * Native cold-start preemption (item 8) covers this at the shell level, so a
 * current binary never renders the start page first: MainActivity loads the
 * link (or a push tap's destination) as the WebView's first request,
 * consuming the intent so no retained event fires, and
 * HiiveBridgeViewController replaces the in-flight `/` load while the splash
 * is up — for every page path, notification taps included — and marks the
 * link so its retained echo is ignored (`claimNativeColdStartLink`). Older
 * binaries still resolve here exactly as before, with no version gating.
 */

/** Ceiling on the hold. Must stay below the native shell's own splash cap. */
export const MAX_HOLD_MS = 2500;

/**
 * Historical grace for a retained cold-start tap to arrive after its listener
 * attached.
 *
 * Retained for compatibility (tests and any external waiter), but NOTHING
 * should wait on it for splash purposes anymore: `appUrlOpen` is dispatched
 * with `retainUntilConsumed` on both platforms, so attaching the listener IS
 * the delivery trigger and no grace sleep is needed. The deep-link handler no
 * longer sleeps on this value.
 */
export const RETAINED_TAP_GRACE_MS = 120;

let released = false;
let waiters: Array<() => void> = [];
let timer: ReturnType<typeof setTimeout> | null = null;

export function isNativePlatform(): boolean {
  if (typeof window === "undefined") return false;
  return !!(
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor?.isNativePlatform?.();
}

/**
 * Let the splash go. Idempotent, and safe to call from every branch of the
 * deep-link handler — including the ones that run long after the splash is
 * gone, where it is a no-op.
 */
export function releaseNativeSplashGate(): void {
  if (released) return;
  released = true;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const pending = waiters;
  waiters = [];
  for (const resolve of pending) resolve();
}

/**
 * Resolves once launch routing has settled. Immediate off-native and after
 * the gate has been released; otherwise capped at `MAX_HOLD_MS` so a handler
 * that never reports cannot hold a waiter forever. The splash itself no
 * longer waits on this (see native-splash-ready.client.tsx).
 */
export function waitForNativeSplashGate(
  maxHoldMs: number = MAX_HOLD_MS,
): Promise<void> {
  if (released || !isNativePlatform()) return Promise.resolve();

  return new Promise<void>((resolve) => {
    waiters.push(resolve);
    timer ??= setTimeout(releaseNativeSplashGate, maxHoldMs);
  });
}

/** Test-only reset for the module-level gate state. */
export function resetNativeSplashGateForTests(): void {
  released = false;
  waiters = [];
  if (timer) clearTimeout(timer);
  timer = null;
}
